import { useEffect, useRef, useState } from 'react'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import { clsx } from 'clsx'
import { DesignedMap } from './designed-map'

/**
 * A real Google map, with `DesignedMap` as the fallback for every case where a
 * real one cannot be drawn.
 *
 * WHY A FALLBACK RATHER THAN AN ERROR. The map is decoration around the facts
 * that matter — name, address, "Cómo llegar". When it cannot load, the right
 * behaviour is to look deliberate, not to show a grey box, a spinner that never
 * resolves, or an error a player can do nothing about. `DesignedMap` already
 * reads as a styled map, so the screen stays composed either way.
 *
 * It falls back when:
 *  · no `VITE_GOOGLE_MAPS_API_KEY` (a build without one — e.g. a fresh clone)
 *  · the Maps script fails (offline, blocked, over quota, wrong referer)
 *  · no pin has coordinates. 48/48 places are geocoded today, but a place added
 *    by hand tomorrow starts with none, and half a map is worse than none.
 *
 * THE KEY IS PUBLIC BY DESIGN. It ships in the bundle — that is what a browser
 * key is. The protection is the restriction, not secrecy: referer-locked to our
 * own hosts and API-locked to `maps-backend`. The SERVER geocoding key is a
 * different key and must never appear in client code.
 *
 * BILLING. Dynamic Maps bills per map load beyond ~10k/month free — comfortably
 * free at current volume, and the billing account carries a €50 alert. It is
 * still why the loader is module-level (one script per session, shared by every
 * caller) and why the effect keys on pin CONTENT: a re-render must not tear the
 * map down and re-create it, because that bills a second load for one screen.
 */
export interface MapPin {
  lat: number
  lng: number
  title?: string
}

/**
 * Loaded once per session and shared, mirroring clubmanager's
 * `google-maps-loader.ts`. Resolves to null with no key so callers fall back
 * instead of throwing.
 */
let mapsPromise: Promise<typeof google.maps | null> | null = null

function loadMaps(): Promise<typeof google.maps | null> {
  if (mapsPromise) return mapsPromise
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
  if (!apiKey) {
    mapsPromise = Promise.resolve(null)
    return mapsPromise
  }
  setOptions({ key: apiKey, v: 'weekly' })
  mapsPromise = importLibrary('maps').then(() => google.maps)
  return mapsPromise
}

export function isMapConfigured(): boolean {
  return Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY)
}

/** Dark styling to match the shell; Google's default light map fights it. */
const DARK_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1d2126' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1d2126' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8b949e' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2f36' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6e7681' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#11151a' }] },
]

export function LiveMap({
  pins,
  className,
  label,
  zoom = 14,
}: {
  pins: MapPin[]
  className?: string
  label?: string
  zoom?: number
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [failed, setFailed] = useState(false)
  const usable = pins.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
  const key = usable.map((p) => `${p.lat},${p.lng}`).join('|')

  useEffect(() => {
    if (!isMapConfigured() || usable.length === 0) return
    let cancelled = false
    loadMaps()
      .then((maps) => {
        if (cancelled || !maps || !ref.current) {
          if (!cancelled && !maps) setFailed(true)
          return
        }
        const first = usable[0]!
        const map = new maps.Map(ref.current, {
          center: { lat: first.lat, lng: first.lng },
          zoom,
          styles: DARK_STYLE,
          disableDefaultUI: true,
          // Cooperative: a one-finger drag scrolls the PAGE, not the map. On a
          // phone the map sits inside a scrolling column, and a map that traps
          // the scroll is the classic way to strand somebody mid-page.
          gestureHandling: 'cooperative',
        })
        // `Marker` is flagged deprecated in favour of `AdvancedMarkerElement`,
        // and that swap is NOT free: advanced markers require a cloud `mapId`,
        // and a map with a mapId ignores inline `styles`. Taking it would move
        // the dark theme out of this file and into the Cloud console, where it
        // is invisible to review and drifts from the design tokens. Deliberate
        // trade — revisit only if styling moves to cloud map IDs anyway.
        for (const p of usable) {
          new maps.Marker({
            map,
            position: { lat: p.lat, lng: p.lng },
            ...(p.title ? { title: p.title } : {}),
          })
        }
        if (usable.length > 1) {
          const bounds = new maps.LatLngBounds()
          for (const p of usable) bounds.extend({ lat: p.lat, lng: p.lng })
          map.fitBounds(bounds, 32)
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
    // Keyed on pin CONTENT, not the array identity — see the billing note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, zoom])

  if (!isMapConfigured() || failed || usable.length === 0) {
    return (
      <DesignedMap
        {...(className ? { className } : {})}
        pins={usable.length}
        {...(label ? { label } : {})}
      />
    )
  }
  return (
    <div
      ref={ref}
      className={clsx(
        'overflow-hidden rounded-[var(--radius-card)] border border-[var(--line)]',
        className,
      )}
      role="img"
      aria-label={label ?? 'Mapa'}
    />
  )
}
